/*
 * The World Editor's Add object icons, drawn in code.
 *
 * Each icon pictures the object its button creates, in the cartoon style of
 * "Blitz3D+ Basics: Your First 3D Lesson" (help/lessons/first-3d-lesson): the
 * same primitive meshes, toon shading, dark outlines, pivot, sun and movie
 * camera, and the same colours the lesson paints its cube, sphere, cylinder
 * and cone. The meshes and renderer below are the lesson's, cut down to its
 * 'toon' style. The icons sit on light button faces rather than the lesson's
 * dark stage, so lines that the lesson draws plain (axes, sun rays) get the
 * same dark outline as its solid shapes.
 *
 * tools/Update-WorldEditorIcons.ps1 draws the strip and saves it as
 * _release/world-editor/icons/add_object.png. Keep the icon order in
 * step with WAC_ICON_* in _release/world-editor/editor_style.bb.
 *
 * WorldEditorIcons.draw(ctx, index, x, y, size) draws one icon in a square.
 * Icons are designed on a 128-pixel square and shown at 32 logical pixels,
 * so outlines are about four times the width they have on screen.
 *
 * Blip draws icons of his own in the same style, such as a recipe's tile
 * icon, from the shapes the palette's are made of:
 * WorldEditorIcons.drawShapes(ctx, shapes, view, x, y, size), which Blip's
 * page bundles (web/ide/src/blip/icons.js, his draw_icon).
 */
(function (global) {
'use strict';

let ctx = null;

// ------------------------------------------------------------------ palette

// The help palette (help/css/blitz-help.css) that the lesson reads.
const C = {
    deep: '#12344f',
    white: '#ffffff',
    key: '#aaffff',
    comment: '#ffee00',
    str: '#00ff66',
    raised: '#2b6394',
};
// Most 3D tools draw the x axis red, y green and z blue, and so does the lesson.
const AXIS = { x: '#ff6f61', y: C.str, z: '#58a6ff' };

function rgbOf(c) {
    const n = parseInt(c.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function hex(r, g, b) {
    const v = (x) => Math.max(0, Math.min(255, Math.round(x)));
    return '#' + ((1 << 24) | (v(r) << 16) | (v(g) << 8) | v(b)).toString(16).slice(1);
}
function mix(a, b, t) {
    const A = rgbOf(a), B = rgbOf(b);
    return hex(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t);
}
function fade(c, a) {
    const A = rgbOf(c);
    return 'rgba(' + A[0] + ',' + A[1] + ',' + A[2] + ',' + a + ')';
}
// The lesson's EntityColor values.
const RGB = (r, g, b) => hex(r, g, b);
const YELLOW = RGB(255, 238, 0);
const TEAL = RGB(51, 255, 221);
const CUBE_BLUE = RGB(0, 150, 255);
const CAN_RED = RGB(255, 90, 90);
const CONE_GREEN = RGB(0, 255, 102);
const GROUND_GREEN = RGB(0, 120, 60);
const SPARK_ORANGE = RGB(255, 140, 40);

// -------------------------------------------------------------------- maths

const DEG = Math.PI / 180;
function clamp(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }
function lerp(a, b, t) { return a + (b - a) * t; }
function matMul(A, B) {
    const R = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) R[i][j] = A[i][0] * B[0][j] + A[i][1] * B[1][j] + A[i][2] * B[2][j];
    }
    return R;
}
function apply(M, v) {
    return [
        M[0][0] * v[0] + M[0][1] * v[1] + M[0][2] * v[2],
        M[1][0] * v[0] + M[1][1] * v[1] + M[1][2] * v[2],
        M[2][0] * v[0] + M[2][1] * v[1] + M[2][2] * v[2],
    ];
}
// The transpose of M times v: for a rotation, the turn undone.
function applyT(M, v) {
    return [
        M[0][0] * v[0] + M[1][0] * v[1] + M[2][0] * v[2],
        M[0][1] * v[0] + M[1][1] * v[1] + M[2][1] * v[2],
        M[0][2] * v[0] + M[1][2] * v[1] + M[2][2] * v[2],
    ];
}
function add3(a, b) { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; }
function sub3(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }
function mul3(a, k) { return [a[0] * k, a[1] * k, a[2] * k]; }
function lerp3(a, b, t) { return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)]; }
function cross3(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
function norm3(v) { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; }
function dot3(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
function inverse3(M) {
    const [a, b, c] = M[0], [d, e, f] = M[1], [g, h, i] = M[2];
    const A = e * i - f * h, B = f * g - d * i, K = d * h - e * g;
    const det = a * A + b * B + c * K || 1e-9;
    return [
        [A / det, (c * h - b * i) / det, (b * f - c * e) / det],
        [B / det, (a * i - c * g) / det, (c * d - a * f) / det],
        [K / det, (b * g - a * h) / det, (a * e - b * d) / det],
    ];
}

// Blitz3D+'s turns: pitch about x (positive tips the front down), yaw about
// y (positive turns left) and roll about z (positive lifts the right side).
function bRotX(a) { const c = Math.cos(a * DEG), s = Math.sin(a * DEG); return [[1, 0, 0], [0, c, -s], [0, s, c]]; }
function bRotY(a) { const c = Math.cos(a * DEG), s = Math.sin(a * DEG); return [[c, 0, -s], [0, 1, 0], [s, 0, c]]; }
function bRotZ(a) { const c = Math.cos(a * DEG), s = Math.sin(a * DEG); return [[c, -s, 0], [s, c, 0], [0, 0, 1]]; }
function bRot(r) { return matMul(bRotY(r[1] || 0), matMul(bRotX(r[0] || 0), bRotZ(r[2] || 0))); }
// M with any scale taken out, leaving just the turn.
function rotOnly(M) {
    const k = [0, 1, 2].map((j) => Math.hypot(M[0][j], M[1][j], M[2][j]) || 1);
    return M.map((row) => [row[0] / k[0], row[1] / k[1], row[2] / k[2]]);
}

// An entity: a kind of mesh, a position, a pitch, yaw and roll and a scale.
function ent(mesh, o) {
    return Object.assign({ mesh, segs: 16, pos: [0, 0, 0], rot: [0, 0, 0], scale: [1, 1, 1], colour: C.white }, o || {});
}
function worldXf(e) {
    const R = bRot(e.rot), s = e.scale;
    return { M: R.map((row) => [row[0] * s[0], row[1] * s[1], row[2] * s[2]]), T: e.pos };
}
function xfPoint(X, v) { return add3(apply(X.M, v), X.T); }

// ------------------------------------------------------------------- meshes

const CUBE_V = [[-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1], [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]];
const CUBE_F = [
    { v: [4, 5, 6, 7], n: [0, 0, 1] },
    { v: [1, 0, 3, 2], n: [0, 0, -1] },
    { v: [5, 1, 2, 6], n: [1, 0, 0] },
    { v: [0, 4, 7, 3], n: [-1, 0, 0] },
    { v: [7, 6, 2, 3], n: [0, 1, 0] },
    { v: [0, 1, 5, 4], n: [0, -1, 0] },
];
// Primitives shaped like Blitz3D+'s: a cube from -1 to 1, a sphere of radius
// 1, a cylinder and cone 2 units tall with a radius of 1, and CreateQuad's
// 2 by 2 square lying in the x-z plane. Faces are wound clockwise as seen
// from outside, and each remembers its surface: a cylinder's side is surface
// 1 and its ends surface 2, just as CreateCylinder makes them.
const meshCache = {};
function meshOf(kind, segs) {
    const key = kind + ':' + (segs || 0);
    return meshCache[key] || (meshCache[key] = buildMesh(kind, segs || 16));
}
function buildMesh(kind, n) {
    const verts = [], faces = [];
    const face = (v, surf, c) => faces.push({ v, surf: surf || 1, c: c || [0, 0, 0] });
    const box = (a, b, surf) => {
        const base = verts.length;
        CUBE_V.forEach((v) => verts.push([v[0] < 0 ? a[0] : b[0], v[1] < 0 ? a[1] : b[1], v[2] < 0 ? a[2] : b[2]]));
        CUBE_F.forEach((f) => face(f.v.map((i) => base + i), surf, lerp3(a, b, 0.5)));
    };
    // A round tube along one axis: 0 is x, 1 is y. Its ends are 'capped'.
    const tube = (axis, c, r, half, segs, surf, capped) => {
        const base = verts.length;
        const at = (s, end) => {
            const ph = Math.PI * 2 * s / segs, u = Math.cos(ph) * r, w = Math.sin(ph) * r;
            return axis === 1 ? [c[0] + u, c[1] + end * half, c[2] + w] : [c[0] + end * half, c[1] + u, c[2] + w];
        };
        for (let s = 0; s < segs; s++) verts.push(at(s, -1));
        for (let s = 0; s < segs; s++) verts.push(at(s, 1));
        for (let s = 0; s < segs; s++) face([base + s, base + (s + 1) % segs, base + segs + (s + 1) % segs, base + segs + s], surf, c);
        if (capped) {
            face(Array.from({ length: segs }, (_, s) => base + s), capped, c);
            face(Array.from({ length: segs }, (_, s) => base + segs + s), capped, c);
        }
    };
    if (kind === 'cube') {
        box([-1, -1, -1], [1, 1, 1], 1);
    } else if (kind === 'cylinder') {
        tube(1, [0, 0, 0], 1, 1, n, 1, 2);
    } else if (kind === 'cone') {
        for (let s = 0; s < n; s++) {
            const ph = Math.PI * 2 * s / n;
            verts.push([Math.cos(ph), -1, Math.sin(ph)]);
        }
        verts.push([0, 1, 0]);
        for (let s = 0; s < n; s++) face([s, (s + 1) % n, n], 1);
        face(Array.from({ length: n }, (_, s) => s), 2);
    } else if (kind === 'quad') {
        verts.push([-1, 0, -1], [1, 0, -1], [1, 0, 1], [-1, 0, 1]);
        face([0, 1, 2, 3], 1, [0, -1, 0]);
    } else if (kind === 'capsule') {
        // CreateCapsule's defaults: radius 0.5 and 2 high, ends and all.
        const r = 0.5, half = 0.5, rows = 8, ring = [];
        for (let i = 0; i <= rows; i++) {
            const a = -Math.PI / 2 + Math.PI * i / rows, y = Math.sin(a) * r + (i <= rows / 2 ? -half : half);
            ring.push([Math.cos(a) * r, y]);
            if (i === rows / 2) ring.push([r, half]);
        }
        ring.forEach(([rr, y]) => { for (let s = 0; s < n; s++) { const ph = Math.PI * 2 * s / n; verts.push([Math.cos(ph) * rr, y, Math.sin(ph) * rr]); } });
        for (let i = 0; i + 1 < ring.length; i++) {
            for (let s = 0; s < n; s++) {
                const a = i * n + s, b = i * n + (s + 1) % n;
                // Start each face at a corner that is not a pole, so its normal is defined.
                face(i === 0 ? [b + n, a + n, a, b] : [a, b, b + n, a + n], 1, [0, (ring[i][1] + ring[i + 1][1]) / 2, 0]);
            }
        }
    } else if (kind === 'tube' || kind === 'arch' || kind === 'ring') {
        // A hollow tube along y; an arch is half of one, stood on its side.
        const r0 = kind === 'ring' ? 0.62 : 0.6, r1 = 1, half = kind === 'ring' ? 0.2 : kind === 'arch' ? 0.35 : 1;
        const arc = kind === 'arch' ? Math.PI : Math.PI * 2, segs = kind === 'arch' ? n / 2 : n, closed = kind !== 'arch';
        const count = closed ? segs : segs + 1;
        const at = (s, rad, y) => { const ph = arc * s / segs; return [Math.cos(ph) * rad, y, Math.sin(ph) * rad]; };
        for (let s = 0; s < count; s++) verts.push(at(s, r1, -half), at(s, r1, half), at(s, r0, -half), at(s, r0, half));
        // Each face's hint point lies inside the wall, so its normal faces out.
        for (let s = 0; s < segs; s++) {
            const a = s * 4, b = ((s + 1) % count) * 4;
            face([a, b, b + 1, a + 1], 1, [0, 0, 0]);
            face([a + 2, a + 3, b + 3, b + 2], 2, mul3(at(s + 0.5, 1, 0), 3));
            face([a + 1, b + 1, b + 3, a + 3], 3, sub3(at(s + 0.5, 0.8, 0), [0, 1, 0]));
            face([a, a + 2, b + 2, b], 3, add3(at(s + 0.5, 0.8, 0), [0, 1, 0]));
        }
        // An open arc's cut ends both face away from the solid, along -z.
        if (!closed) [0, segs * 4].forEach((a, end) => face([a, a + 1, a + 3, a + 2], 3, add3(at(end ? segs : 0, 0.8, 0), [0, 0, 1])));
    } else if (kind === 'torus') {
        const R = 0.75, r = 0.25, sides = 10;
        for (let s = 0; s < n; s++) {
            const ph = Math.PI * 2 * s / n;
            for (let k = 0; k < sides; k++) {
                const th = Math.PI * 2 * k / sides, rr = R + Math.cos(th) * r;
                verts.push([Math.cos(ph) * rr, Math.sin(th) * r, Math.sin(ph) * rr]);
            }
        }
        for (let s = 0; s < n; s++) {
            const ph = Math.PI * 2 * (s + 0.5) / n, centre = [Math.cos(ph) * R, 0, Math.sin(ph) * R];
            for (let k = 0; k < sides; k++) {
                const a = s * sides + k, b = ((s + 1) % n) * sides + k, c = ((s + 1) % n) * sides + (k + 1) % sides, d = s * sides + (k + 1) % sides;
                face([a, b, c, d], 1, centre);
            }
        }
    } else if (kind === 'wedge') {
        // Full height at the back (+z), sloping down to the front.
        verts.push([-1, -1, -1], [1, -1, -1], [1, -1, 1], [-1, -1, 1], [-1, 1, 1], [1, 1, 1]);
        // The origin lies on the slope, so point the faces from inside instead.
        const inside = [0, -0.5, 0.5];
        face([0, 1, 2, 3], 1, inside);
        face([3, 2, 5, 4], 1, inside);
        face([0, 1, 5, 4], 1, inside);
        face([0, 3, 4], 1, inside);
        face([1, 2, 5], 1, inside);
    } else if (kind === 'pyramid' || kind === 'prism') {
        // A frustum with few segments, turned so a face looks along +z.
        const sides = kind === 'pyramid' ? 4 : 6, top = kind === 'pyramid' ? 0 : 1, turn = Math.PI / 2 - Math.PI / sides;
        for (let s = 0; s < sides; s++) {
            const ph = Math.PI * 2 * s / sides + turn;
            verts.push([Math.cos(ph), -1, Math.sin(ph)]);
        }
        if (top === 0) {
            verts.push([0, 1, 0]);
            for (let s = 0; s < sides; s++) face([s, (s + 1) % sides, sides], 1);
        } else {
            for (let s = 0; s < sides; s++) {
                const ph = Math.PI * 2 * s / sides + turn;
                verts.push([Math.cos(ph), 1, Math.sin(ph)]);
            }
            for (let s = 0; s < sides; s++) face([s, (s + 1) % sides, sides + (s + 1) % sides, sides + s], 1);
            face(Array.from({ length: sides }, (_, s) => sides + s), 2);
        }
        face(Array.from({ length: sides }, (_, s) => s), 2);
    } else if (kind === 'rock') {
        // A boulder for Blip's icons: an icosahedron with its corners pushed
        // in and out, a little flatter than it is wide. n chooses which.
        const t = (1 + Math.sqrt(5)) / 2;
        const ico = [[-1, t, 0], [1, t, 0], [-1, -t, 0], [1, -t, 0], [0, -1, t], [0, 1, t], [0, -1, -t], [0, 1, -t],
            [t, 0, -1], [t, 0, 1], [-t, 0, -1], [-t, 0, 1]];
        let seed = n * 7919 % 233280;
        const random = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
        ico.forEach((v) => {
            const k = (0.82 + random() * 0.3) / Math.hypot(v[0], v[1], v[2]);
            verts.push([v[0] * k, v[1] * k * 0.78, v[2] * k]);
        });
        [[0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11], [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
            [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9], [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1]]
            .forEach((v) => face(v, 1));
    } else if (kind === 'camera') {
        // A little movie camera looking along +z: a body, a lens and two reels.
        box([-0.3, -0.22, -0.55], [0.3, 0.22, 0.05], 1);
        const base = verts.length, r0 = 0.12, r1 = 0.21, z0 = 0.05, z1 = 0.34;
        CUBE_V.forEach((v) => {
            const r = v[2] < 0 ? r0 : r1;
            verts.push([v[0] * r, v[1] * r, v[2] < 0 ? z0 : z1]);
        });
        CUBE_F.forEach((f) => face(f.v.map((i) => base + i), 2, [0, 0, (z0 + z1) / 2]));
        tube(0, [0, 0.39, -0.38], 0.17, 0.06, 12, 3, 3);
        tube(0, [0, 0.36, -0.04], 0.14, 0.06, 12, 3, 3);
    }
    faces.forEach((f) => {
        const a = verts[f.v[0]], b = verts[f.v[1]], c = verts[f.v[2]];
        let m = [0, 0, 0];
        f.v.forEach((i) => { m = add3(m, verts[i]); });
        m = sub3(mul3(m, 1 / f.v.length), f.c);
        if (dot3(cross3(sub3(b, a), sub3(c, a)), m) < 0) f.v.reverse();
    });
    return { verts, faces };
}

// -------------------------------------------------------------------- views

// A view looks at a target from a pitch, a yaw and a distance; its focal
// length in pixels sets the strength of the perspective.
function orbitView(o) {
    const R = bRot([o.pitch, o.yaw, 0]);
    const eye = sub3([o.tx || 0, o.ty || 0, o.tz || 0], mul3(apply(R, [0, 0, 1]), o.dist));
    return { R, eye, f: o.focal, cx: o.cx, cy: o.cy, near: 0.1 };
}
function toView(view, w) { return applyT(view.R, sub3(w, view.eye)); }
function projectV(view, v) { return [view.cx + view.f * v[0] / v[2], view.cy - view.f * v[1] / v[2]]; }
function project(view, w) {
    const v = toView(view, w);
    if (v[2] <= view.near) return null;
    return { x: view.cx + view.f * v[0] / v[2], y: view.cy - view.f * v[1] / v[2], z: v[2], k: view.f / v[2] };
}
// Keeps the part of a polygon in front of the eye.
function clipNear(poly, near) {
    const out = [];
    for (let i = 0; i < poly.length; i++) {
        const a = poly[i], b = poly[(i + 1) % poly.length];
        const ina = a[2] >= near, inb = b[2] >= near;
        if (ina) out.push(a);
        if (ina !== inb) out.push(lerp3(a, b, (near - a[2]) / (b[2] - a[2])));
    }
    return out;
}
// A line between two world points, cut off where it passes behind the eye.
function line3(view, a, b) {
    let va = toView(view, a), vb = toView(view, b);
    const n = view.near;
    if (va[2] < n && vb[2] < n) return null;
    if (va[2] < n) va = lerp3(va, vb, (n - va[2]) / (vb[2] - va[2]));
    else if (vb[2] < n) vb = lerp3(vb, va, (n - vb[2]) / (va[2] - vb[2]));
    return [projectV(view, va), projectV(view, vb)];
}

// ------------------------------------------------------------------ shading

// The lesson's cartoon light comes from the upper left, in front.
const STAGE_LIGHT = norm3([-0.45, 0.75, -0.5]);
function shadeToon(base, n, light) {
    const dif = Math.max(0, dot3(n, light || STAGE_LIGHT));
    let col = mix(C.deep, base, 0.42 + 0.58 * dif);
    if (dif > 0.85) col = mix(col, C.white, (dif - 0.85) * 1.4);
    return col;
}

// ---------------------------------------------------------------- rendering

// Each icon is scaled to fill the same square, but its lines keep their
// intended width: lw() converts a width in design pixels (on the finished
// 128-pixel icon) to the icon's own drawing units.
let lineScale = 1;
function lw(width) { return width * lineScale; }

function poly(pts) {
    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
    ctx.closePath();
}
// Draws entities as a view sees them, the one whose middle is furthest
// first, in the lesson's cartoon style: toon-shaded faces over a thick dark
// outline. An entity's layer comes first: lower layers are drawn under the
// rest, as the ground is under what stands on it, however near its middle
// is.
function renderScene(view, ents) {
    const groups = [];
    for (const e of ents) {
        const X = worldXf(e);
        // A sphere scaled evenly is drawn as a smoothly lit disc.
        if (e.mesh === 'sphere') {
            groups.push({ e, sphere: X, depth: toView(view, X.T)[2] });
            continue;
        }
        const mesh = meshOf(e.mesh, e.segs);
        const W = mesh.verts.map((v) => xfPoint(X, v));
        const V = W.map((w) => toView(view, w));
        const faces = [];
        for (const f of mesh.faces) {
            const w0 = W[f.v[0]];
            const n = norm3(cross3(sub3(W[f.v[1]], w0), sub3(W[f.v[2]], w0)));
            const facing = dot3(n, sub3(view.eye, w0)) > 0;
            if (!facing && !e.twoSided) continue;
            let depth = 0;
            f.v.forEach((i) => { depth += V[i][2]; });
            const base = (e.surf && e.surf[f.surf]) || e.colour;
            faces.push({
                pts: f.v.map((i) => projectV(view, V[i])),
                depth: depth / f.v.length,
                colour: shadeToon(base, facing ? n : mul3(n, -1), e.light),
            });
        }
        faces.sort((a, b) => b.depth - a.depth);
        groups.push({ e, faces, depth: toView(view, X.T)[2] });
    }
    groups.sort((a, b) => (a.e.layer || 0) - (b.e.layer || 0) || b.depth - a.depth);
    for (const g of groups) {
        if (g.sphere) (evenScale(g.sphere.M) ? drawSmoothSphere : drawSmoothEllipsoid)(view, g.sphere, g.e);
        else drawGroup(g);
    }
}
// Whether a transform scales every way alike.
function evenScale(M) {
    const k = [0, 1, 2].map((j) => Math.hypot(M[0][j], M[1][j], M[2][j]));
    return Math.max(...k) - Math.min(...k) <= 1e-6 * Math.max(...k);
}
function drawGroup(g) {
    const e = g.e;
    ctx.save();
    ctx.lineJoin = 'round';
    // A thick dark stroke under every face shows round the edge as an outline.
    ctx.strokeStyle = C.deep;
    ctx.lineWidth = lw(OUTLINE * 2);
    for (const f of g.faces) { poly(f.pts); ctx.stroke(); }
    for (const f of g.faces) {
        poly(f.pts);
        ctx.fillStyle = f.colour;
        ctx.fill();
        ctx.lineWidth = lw(1);
        ctx.strokeStyle = f.colour;
        ctx.stroke();
    }
    if (e.edges) {
        ctx.strokeStyle = fade(C.deep, 0.45);
        ctx.lineWidth = lw(EDGE);
        for (const f of g.faces) { poly(f.pts); ctx.stroke(); }
    }
    ctx.restore();
}
// A sphere, lit pixel by pixel with the lesson's cartoon light, and outlined.
function drawSmoothSphere(view, X, e) {
    const radius = Math.hypot(X.M[0][0], X.M[1][0], X.M[2][0]);
    const q = project(view, X.T);
    const r = radius * q.k;
    const n = Math.max(8, Math.ceil(2 * r * Math.abs(ctx.getTransform().a)));
    const cv = document.createElement('canvas');
    cv.width = n;
    cv.height = n;
    const c2 = cv.getContext('2d'), img = c2.createImageData(n, n), px = img.data;
    const B = rgbOf(e.colour), D = rgbOf(C.deep), Wt = rgbOf(C.white), R = view.R, Lt = e.light || STAGE_LIGHT;
    for (let j = 0; j < n; j++) {
        const dy = (j + 0.5) / n * 2 - 1;
        for (let i = 0; i < n; i++) {
            const dx = (i + 0.5) / n * 2 - 1, d2 = dx * dx + dy * dy, at = (j * n + i) * 4;
            if (d2 >= 1) { px[at + 3] = 0; continue; }
            const nz = Math.sqrt(1 - d2);
            const nx = R[0][0] * dx - R[0][1] * dy - R[0][2] * nz;
            const ny = R[1][0] * dx - R[1][1] * dy - R[1][2] * nz;
            const nz2 = R[2][0] * dx - R[2][1] * dy - R[2][2] * nz;
            const dif = Math.max(0, nx * Lt[0] + ny * Lt[1] + nz2 * Lt[2]), k = 0.42 + 0.58 * dif;
            let r0 = D[0] + (B[0] - D[0]) * k, g0 = D[1] + (B[1] - D[1]) * k, b0 = D[2] + (B[2] - D[2]) * k;
            if (dif > 0.85) {
                const w = Math.min(1, (dif - 0.85) * 1.4);
                r0 += (Wt[0] - r0) * w;
                g0 += (Wt[1] - g0) * w;
                b0 += (Wt[2] - b0) * w;
            }
            px[at] = r0;
            px[at + 1] = g0;
            px[at + 2] = b0;
            px[at + 3] = 255 * clamp((1 - Math.sqrt(d2)) * n / 2);
        }
    }
    c2.putImageData(img, 0, 0);
    ctx.drawImage(cv, q.x - r, q.y - r, 2 * r, 2 * r);
    // The ring overlaps the disc a little, so no light shows between them.
    ctx.beginPath();
    ctx.arc(q.x, q.y, r + lw(OUTLINE) * 0.3, 0, Math.PI * 2);
    ctx.lineWidth = lw(OUTLINE * 1.6);
    ctx.strokeStyle = C.deep;
    ctx.stroke();
}
// A sphere scaled unevenly, such as a pebble or a hill: the ellipse it makes
// on the icon, lit pixel by pixel as the sphere is, and outlined. Like the
// sphere, it is seen as from straight ahead of its middle.
function drawSmoothEllipsoid(view, X, e) {
    const q = project(view, X.T);
    // The unit sphere it is made from, turned into the view, and on the icon
    // round its middle (y down).
    const B = [0, 1, 2].map((i) => [0, 1, 2].map((j) =>
        view.R[0][i] * X.M[0][j] + view.R[1][i] * X.M[1][j] + view.R[2][i] * X.M[2][j]));
    const A = [mul3(B[0], q.k), mul3(B[1], -q.k)];
    // The ellipse holds the points s where s S^-1 s <= 1, S being A A^T.
    const s00 = dot3(A[0], A[0]), s01 = dot3(A[0], A[1]), s11 = dot3(A[1], A[1]);
    const det = s00 * s11 - s01 * s01;
    if (!(det > 1e-9)) return;
    const i00 = s11 / det, i01 = -s01 / det, i11 = s00 / det;
    // Points along w share a place on the icon: each pixel's ray, whose
    // nearer end is the one seen.
    const w = cross3(A[0], A[1]), ww = dot3(w, w), toward = dot3(B[2], w) > 0 ? -1 : 1;
    const inverse = inverse3(X.M);
    const hx = Math.sqrt(s00), hy = Math.sqrt(s11), density = Math.abs(ctx.getTransform().a);
    const n = Math.max(8, Math.ceil(2 * hx * density)), m = Math.max(8, Math.ceil(2 * hy * density));
    const cv = document.createElement('canvas');
    cv.width = n;
    cv.height = m;
    const c2 = cv.getContext('2d'), img = c2.createImageData(n, m), px = img.data;
    const Bc = rgbOf(e.colour), D = rgbOf(C.deep), Wt = rgbOf(C.white), Lt = e.light || STAGE_LIGHT;
    const rim = Math.min(n, m) / 2;
    for (let j = 0; j < m; j++) {
        const sy = ((j + 0.5) / m * 2 - 1) * hy;
        for (let i = 0; i < n; i++) {
            const sx = ((i + 0.5) / n * 2 - 1) * hx, at = (j * n + i) * 4;
            const r2 = sx * sx * i00 + 2 * sx * sy * i01 + sy * sy * i11;
            if (r2 >= 1) { px[at + 3] = 0; continue; }
            const a0 = i00 * sx + i01 * sy, a1 = i01 * sx + i11 * sy, t = toward * Math.sqrt((1 - r2) / ww);
            const u = [0, 1, 2].map((k) => A[0][k] * a0 + A[1][k] * a1 + t * w[k]);
            // Its surface faces along the inverse transform's transpose of u.
            const normal = norm3(applyT(inverse, u));
            const dif = Math.max(0, dot3(normal, Lt)), k = 0.42 + 0.58 * dif;
            let r0 = D[0] + (Bc[0] - D[0]) * k, g0 = D[1] + (Bc[1] - D[1]) * k, b0 = D[2] + (Bc[2] - D[2]) * k;
            if (dif > 0.85) {
                const glint = Math.min(1, (dif - 0.85) * 1.4);
                r0 += (Wt[0] - r0) * glint;
                g0 += (Wt[1] - g0) * glint;
                b0 += (Wt[2] - b0) * glint;
            }
            px[at] = r0;
            px[at + 1] = g0;
            px[at + 2] = b0;
            px[at + 3] = 255 * clamp((1 - Math.sqrt(r2)) * rim);
        }
    }
    c2.putImageData(img, 0, 0);
    ctx.drawImage(cv, q.x - hx, q.y - hy, 2 * hx, 2 * hy);
    // The outline, round the ellipse's own axes.
    const half = (s00 + s11) / 2, spread = Math.hypot((s00 - s11) / 2, s01), gap = lw(OUTLINE) * 0.3;
    ctx.beginPath();
    ctx.ellipse(q.x, q.y, Math.sqrt(half + spread) + gap, Math.sqrt(Math.max(0, half - spread)) + gap,
        0.5 * Math.atan2(2 * s01, s00 - s11), 0, Math.PI * 2);
    ctx.lineWidth = lw(OUTLINE * 1.6);
    ctx.strokeStyle = C.deep;
    ctx.stroke();
}
// A line with a dark edge: the outline stroke first, then the colour.
function outlinedLine(a, b, colour, width) {
    ctx.save();
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(a[0], a[1]);
    ctx.lineTo(b[0], b[1]);
    ctx.strokeStyle = C.deep;
    ctx.lineWidth = lw(width + 2 * OUTLINE);
    ctx.stroke();
    ctx.strokeStyle = colour;
    ctx.lineWidth = lw(width);
    ctx.stroke();
    ctx.restore();
}
// An arrow along a projected line, head and all outlined. The widths and
// the head's size are in design pixels.
function outlinedArrow(a, b, colour, width, headSize) {
    const ang = Math.atan2(b[1] - a[1], b[0] - a[0]), head = lw(headSize);
    const tip = [b[0] + Math.cos(ang) * head * 0.2, b[1] + Math.sin(ang) * head * 0.2];
    const base = [b[0] - Math.cos(ang) * head * 0.6, b[1] - Math.sin(ang) * head * 0.6];
    const wing = (s) => [tip[0] - Math.cos(ang + s * 0.5) * head, tip[1] - Math.sin(ang + s * 0.5) * head];
    const shaft = () => { ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(base[0], base[1]); };
    const arrowHead = () => poly([tip, wing(-1), wing(1)]);
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = C.deep;
    ctx.lineWidth = lw(width + 2 * OUTLINE);
    shaft();
    ctx.stroke();
    arrowHead();
    ctx.lineWidth = lw(2 * OUTLINE);
    ctx.stroke();
    ctx.fillStyle = C.deep;
    ctx.fill();
    ctx.strokeStyle = colour;
    ctx.lineWidth = lw(width);
    shaft();
    ctx.stroke();
    arrowHead();
    ctx.fillStyle = colour;
    ctx.fill();
    ctx.restore();
}
// Draws into a layer of its own, then lays the finished layer on the icon,
// so a picture can be faded out as a whole.
function layer(paint, finish) {
    const outer = ctx;
    const cv = document.createElement('canvas');
    cv.width = outer.canvas.width;
    cv.height = outer.canvas.height;
    ctx = cv.getContext('2d');
    ctx.setTransform(outer.getTransform());
    paint();
    ctx.globalCompositeOperation = 'destination-in';
    finish();
    ctx = outer;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(cv, 0, 0);
    ctx.restore();
}

// -------------------------------------------------------------------- icons

// Widths in design pixels on the finished 128-pixel icon, where 4 design
// pixels make one logical pixel of the 32-pixel button icon: the outline
// that shows round each shape is OUTLINE wide.
const OUTLINE = 5;
const EDGE = 3.5;
// Every icon shares one three-quarter view: the lesson's badges look down
// 22 degrees, and a gentle perspective keeps the shapes solid.
function iconView(o) {
    return orbitView(Object.assign({ yaw: 32, pitch: 24, dist: 8, cx: 0, cy: 0, focal: 100 }, o));
}
// The square each icon fills, in design pixels. Round and tall shapes look
// larger than square ones of the same extent, so some icons ask for less.
const FILL = 112;
const ICONS = [
    {
        name: 'Pivot',
        // A pivot has no shape, so the lesson shows a little dashed ring
        // with the pivot's own three axes.
        draw() {
            const view = iconView();
            const o = project(view, [0, 0, 0]);
            // Furthest axis first: z points away from this view.
            ['z', 'x', 'y'].forEach((k) => {
                const tip = project(view, mul3({ x: [1, 0, 0], y: [0, 1, 0], z: [0, 0, 1] }[k], k === 'z' ? 2.3 : 2));
                outlinedArrow([o.x, o.y], [tip.x, tip.y], AXIS[k], 8, 24);
            });
            ctx.save();
            ctx.beginPath();
            ctx.arc(o.x, o.y, lw(26), 0, Math.PI * 2);
            ctx.setLineDash([lw(10.2), lw(10.2)]);
            ctx.lineWidth = lw(6.5);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
            ctx.restore();
            ctx.beginPath();
            ctx.arc(o.x, o.y, lw(8.5), 0, Math.PI * 2);
            ctx.fillStyle = C.white;
            ctx.fill();
            ctx.lineWidth = lw(OUTLINE);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
        },
    },
    {
        name: 'Camera',
        // The lesson's movie camera, side on, lens forward and both reels up.
        draw() {
            const cam = ent('camera', {
                rot: [0, -90, 0], edges: true,
                surf: { 1: mix(C.key, C.raised, 0.45), 2: mix(C.key, C.white, 0.5), 3: mix(C.key, C.raised, 0.7) },
            });
            renderScene(iconView({ yaw: 14, pitch: 12 }), [cam]);
        },
    },
    {
        name: 'Light',
        // New lights shine like the sun, so this is the lesson's sun.
        fill: 116,
        draw() {
            const r = 25;
            for (let i = 0; i < 8; i++) {
                const a = i / 8 * Math.PI * 2 + Math.PI / 8;
                outlinedLine([Math.cos(a) * r * 1.5, Math.sin(a) * r * 1.5],
                    [Math.cos(a) * r * 2.1, Math.sin(a) * r * 2.1], C.comment, 8);
            }
            ctx.beginPath();
            ctx.arc(0, 0, r, 0, Math.PI * 2);
            ctx.fillStyle = C.comment;
            ctx.fill();
            ctx.lineWidth = lw(OUTLINE * 1.4);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
        },
    },
    {
        name: 'Cube',
        fill: 108,
        draw() {
            renderScene(iconView(), [ent('cube', { colour: CUBE_BLUE, edges: true })]);
        },
    },
    {
        name: 'Sphere',
        fill: 106,
        draw() {
            renderScene(iconView(), [ent('sphere', { colour: YELLOW })]);
        },
    },
    {
        name: 'Cylinder',
        draw() {
            renderScene(iconView(), [ent('cylinder', { segs: 40, scale: [0.85, 1, 0.85], colour: CAN_RED })]);
        },
    },
    {
        name: 'Cone',
        draw() {
            renderScene(iconView(), [ent('cone', { segs: 40, colour: CONE_GREEN })]);
        },
    },
    {
        name: 'Quad',
        // CreateQuad's flat 2 by 2 square, lying in the x-z plane.
        draw() {
            renderScene(iconView({ pitch: 42 }), [ent('quad', { colour: TEAL, twoSided: true })]);
        },
    },
    {
        name: 'Plane',
        // A plane goes on for ever: ground stretching away into the
        // distance, its grid fading out instead of ending.
        draw() {
            const view = iconView({ yaw: 0, pitch: 38, dist: 4.2, focal: 100 });
            const X0 = -2, X1 = 2, Z0 = -1.8, Z1 = 9;
            const at = (x, z) => projectV(view, toView(view, [x, 0, z]));
            const near = at(0, Z0), far = at(0, Z1);
            layer(() => {
                const outline = [at(X0, Z0), at(X1, Z0), at(X1, Z1), at(X0, Z1)];
                ctx.lineJoin = 'round';
                poly(outline);
                ctx.strokeStyle = C.deep;
                ctx.lineWidth = lw(OUTLINE * 2);
                ctx.stroke();
                ctx.fillStyle = shadeToon(GROUND_GREEN, [0, 1, 0]);
                ctx.fill();
                ctx.beginPath();
                for (let x = X0 + 1; x < X1; x += 1) {
                    const a = at(x, Z0), b = at(x, Z1);
                    ctx.moveTo(a[0], a[1]);
                    ctx.lineTo(b[0], b[1]);
                }
                for (let z = Z0 + 1; z < Z1; z += 1) {
                    const a = at(X0, z), b = at(X1, z);
                    ctx.moveTo(a[0], a[1]);
                    ctx.lineTo(b[0], b[1]);
                }
                ctx.lineWidth = lw(EDGE);
                ctx.strokeStyle = fade(C.deep, 0.5);
                ctx.stroke();
            }, () => {
                const g = ctx.createLinearGradient(0, near[1], 0, far[1]);
                g.addColorStop(0, 'rgba(0,0,0,1)');
                g.addColorStop(0.4, 'rgba(0,0,0,1)');
                g.addColorStop(0.95, 'rgba(0,0,0,0)');
                ctx.fillStyle = g;
                ctx.fillRect(-1000, -1000, 2000, 2000);
            });
        },
    },
    {
        name: 'Particles',
        // A spray of sparks: an emitter on the ground throwing bright
        // specks up and out, fading to orange as they go.
        fill: 110,
        draw() {
            const speck = (x, y, r, colour) => {
                ctx.beginPath();
                ctx.arc(x, y, r, 0, Math.PI * 2);
                ctx.fillStyle = colour;
                ctx.fill();
                ctx.lineWidth = lw(OUTLINE * 0.9);
                ctx.strokeStyle = C.deep;
                ctx.stroke();
            };
            // The emitter: a little dark-rimmed disc seen from the side.
            ctx.beginPath();
            ctx.ellipse(0, 40, 22, 8, 0, 0, Math.PI * 2);
            ctx.fillStyle = mix(C.key, C.raised, 0.45);
            ctx.fill();
            ctx.lineWidth = lw(OUTLINE);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
            // Specks fan out from the emitter, smaller and redder the
            // further they have flown.
            [-36, -12, 12, 36].forEach((angle, row) => {
                [32, 60, 88].forEach((distance, step) => {
                    // Alternate rows start a little further out, so the
                    // specks scatter rather than line up.
                    const d = distance + (row % 2) * 12, a = angle * DEG;
                    speck(Math.sin(a) * d, 30 - Math.cos(a) * d, 11 - 2.5 * step, mix(YELLOW, SPARK_ORANGE, step / 2));
                });
            });
        },
    },
    {
        name: 'Capsule',
        draw() {
            renderScene(iconView(), [ent('capsule', { segs: 32, colour: TEAL })]);
        },
    },
    {
        name: 'Tube',
        draw() {
            renderScene(iconView({ pitch: 34 }), [ent('tube', { segs: 32, colour: CAN_RED,
                surf: { 1: CAN_RED, 2: mix(CAN_RED, C.deep, 0.35), 3: mix(CAN_RED, C.white, 0.35) } })]);
        },
    },
    {
        name: 'Torus',
        draw() {
            renderScene(iconView({ pitch: 38 }), [ent('torus', { segs: 32, colour: SPARK_ORANGE })]);
        },
    },
    {
        name: 'Wedge',
        fill: 108,
        draw() {
            renderScene(iconView({ yaw: -50 }), [ent('wedge', { colour: CUBE_BLUE, edges: true })]);
        },
    },
    {
        name: 'Path',
        // A path: a smooth line through a few points, with a rod laid along it.
        fill: 112,
        draw() {
            const pts = [[-52, 30], [-18, -26], [20, 24], [54, -30]];
            const curve = [];
            for (let i = 0; i < pts.length - 1; i++) {
                const p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(pts.length - 1, i + 2)];
                for (let t = 0; t < 1; t += 0.05) {
                    const t2 = t * t, t3 = t2 * t;
                    curve.push([0, 1].map((k) => 0.5 * (2 * p1[k] + (-p0[k] + p2[k]) * t +
                        (2 * p0[k] - 5 * p1[k] + 4 * p2[k] - p3[k]) * t2 + (-p0[k] + 3 * p1[k] - 3 * p2[k] + p3[k]) * t3)));
                }
            }
            curve.push(pts[pts.length - 1]);
            const stroke = (colour, width) => {
                ctx.beginPath();
                curve.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
                ctx.strokeStyle = colour;
                ctx.lineWidth = lw(width);
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.stroke();
            };
            stroke(C.deep, 18 + 2 * OUTLINE);
            stroke(CAN_RED, 18);
            stroke(mix(CAN_RED, C.white, 0.45), 5);
            pts.forEach((p) => {
                ctx.beginPath();
                ctx.arc(p[0], p[1], lw(9), 0, Math.PI * 2);
                ctx.fillStyle = C.white;
                ctx.fill();
                ctx.lineWidth = lw(OUTLINE);
                ctx.strokeStyle = C.deep;
                ctx.stroke();
            });
        },
    },
    {
        name: 'Pyramid',
        fill: 108,
        draw() {
            renderScene(iconView({ yaw: 24 }), [ent('pyramid', { colour: YELLOW, edges: true })]);
        },
    },
    {
        name: 'Prism',
        fill: 108,
        draw() {
            renderScene(iconView(), [ent('prism', { colour: CONE_GREEN, edges: true,
                surf: { 1: CONE_GREEN, 2: mix(CONE_GREEN, C.white, 0.3) } })]);
        },
    },
    {
        name: 'Arch',
        draw() {
            renderScene(iconView({ yaw: 20, pitch: 16 }), [ent('arch', { segs: 32, rot: [-90, 0, 0], colour: mix(CAN_RED, YELLOW, 0.5),
                surf: { 1: mix(CAN_RED, YELLOW, 0.5), 2: mix(mix(CAN_RED, YELLOW, 0.5), C.deep, 0.3), 3: mix(mix(CAN_RED, YELLOW, 0.5), C.white, 0.3) } })]);
        },
    },
    {
        name: 'Ring',
        draw() {
            renderScene(iconView({ pitch: 40 }), [ent('ring', { segs: 32, colour: TEAL,
                surf: { 1: TEAL, 2: mix(TEAL, C.deep, 0.3), 3: mix(TEAL, C.white, 0.35) } })]);
        },
    },
    {
        name: 'Terrain',
        // A patch of rolling ground: two hills and a dip between them, drawn
        // cell by cell from the back so nearer slopes cover farther ones.
        draw() {
            const view = iconView({ yaw: 25, pitch: 24, dist: 6.4, focal: 118 });
            const N = 14, S = 2.2;
            const h = (x, z) => 1.9 * Math.exp(-((x + 0.5) ** 2 + (z - 0.4) ** 2) * 0.8) +
                1.0 * Math.exp(-((x - 1.2) ** 2 + (z + 1.0) ** 2) * 1.3) - 0.6;
            const point = (i, j) => { const x = -S + 2 * S * i / N, z = -S + 2 * S * j / N; return [x, h(x, z), z]; };
            const at = (w) => projectV(view, toView(view, w));
            const cells = [];
            for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
                const a = point(i, j), b = point(i + 1, j), c = point(i + 1, j + 1), d = point(i, j + 1);
                const n = norm3(cross3(sub3(d, a), sub3(b, a)));
                const centre = mul3(add3(add3(a, b), add3(c, d)), 0.25);
                cells.push({ pts: [a, b, c, d], n, depth: toView(view, centre)[2] });
            }
            ctx.lineJoin = 'round';
            // Every cell's outline first, thick, so the whole shape - hills
            // above the far edge included - has one dark rim once filled.
            for (const cell of cells) {
                poly(cell.pts.map(at));
                ctx.strokeStyle = C.deep;
                ctx.lineWidth = lw(OUTLINE * 2);
                ctx.stroke();
            }
            cells.sort((p, q) => q.depth - p.depth);
            for (const cell of cells) {
                poly(cell.pts.map(at));
                // Higher ground is lighter, like grass catching the sun.
                const height = (cell.pts[0][1] + cell.pts[2][1]) * 0.5;
                const colour = shadeToon(mix(GROUND_GREEN, C.white, clamp(height * 0.18)), cell.n);
                ctx.fillStyle = colour;
                ctx.fill();
                ctx.strokeStyle = colour;
                ctx.lineWidth = lw(1.5);
                ctx.stroke();
            }
        },
    },
    {
        name: 'Zone',
        // A zone is a region, not a solid: a see-through box with dashed
        // edges, and a sparkle inside for the look it brings in.
        draw() {
            const view = iconView({ pitch: 26 });
            const S = [1.5, 0.95, 1.5];
            const at = (v) => { const p = project(view, v); return [p.x, p.y]; };
            const corner = (i) => at(CUBE_V[i].map((c, k) => c * S[k]));
            const facing = CUBE_F.filter((f) => dot3(f.n, sub3(view.eye, f.n.map((c, k) => c * S[k]))) > 0);
            const edges = [], front = {};
            CUBE_F.forEach((f) => f.v.forEach((a, i) => {
                const b = f.v[(i + 1) % 4], key = Math.min(a, b) + '-' + Math.max(a, b);
                if (!edges.includes(key)) edges.push(key);
                if (facing.includes(f)) front[key] = true;
            }));
            const stroke = (keys) => {
                ctx.beginPath();
                keys.forEach((key) => {
                    const [a, b] = key.split('-').map(Number), p = corner(a), q = corner(b);
                    ctx.moveTo(p[0], p[1]);
                    ctx.lineTo(q[0], q[1]);
                });
            };
            ctx.save();
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            // The far edges, faint, seen through the box.
            ctx.setLineDash([lw(10), lw(10)]);
            stroke(edges.filter((key) => !front[key]));
            ctx.lineWidth = lw(EDGE);
            ctx.strokeStyle = fade(C.deep, 0.45);
            ctx.stroke();
            ctx.setLineDash([]);
            // The near faces, tinted glass.
            facing.forEach((f) => {
                poly(f.v.map(corner));
                ctx.fillStyle = fade(shadeToon(TEAL, f.n), 0.42);
                ctx.fill();
            });
            // The near edges, dashed: dark under a light stripe.
            ctx.setLineDash([lw(16), lw(11)]);
            stroke(edges.filter((key) => front[key]));
            ctx.lineWidth = lw(EDGE + 2 * OUTLINE);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
            ctx.lineWidth = lw(EDGE);
            ctx.strokeStyle = mix(TEAL, C.white, 0.35);
            ctx.stroke();
            ctx.setLineDash([]);
            // A four-pointed sparkle in the middle, in front of the near
            // corner so it shows whichever way the edges fall.
            const o = at([0, 0, 0]), big = 19, small = 5.5;
            const star = [];
            for (let i = 0; i < 8; i++) {
                const r = i % 2 ? small : big, a = i * Math.PI / 4;
                star.push([o[0] + Math.sin(a) * r, o[1] - Math.cos(a) * r]);
            }
            poly(star);
            ctx.lineWidth = lw(OUTLINE * 2);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
            ctx.fillStyle = YELLOW;
            ctx.fill();
            ctx.restore();
        },
    },
    {
        name: 'Reflection probe',
        // A mirror ball, as a reflection probe sees the world: the sky in
        // its top half, the ground curving round its bottom half, and a
        // bright highlight.
        draw() {
            const R = 50;
            const ball = () => { ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2); };
            ctx.save();
            ball();
            ctx.lineWidth = lw(OUTLINE * 2);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
            ball();
            ctx.clip();
            const sky = ctx.createLinearGradient(0, -R, 0, R * 0.2);
            sky.addColorStop(0, mix(CUBE_BLUE, C.white, 0.25));
            sky.addColorStop(1, mix(CUBE_BLUE, C.white, 0.85));
            ctx.fillStyle = sky;
            ctx.fillRect(-R, -R, 2 * R, 2 * R);
            // The horizon bows down round the ball, the ground below it.
            ctx.beginPath();
            ctx.moveTo(-R, 2);
            ctx.quadraticCurveTo(0, R * 0.42, R, 2);
            ctx.lineTo(R, R);
            ctx.lineTo(-R, R);
            ctx.closePath();
            const ground = ctx.createLinearGradient(0, 0, 0, R);
            ground.addColorStop(0, mix(GROUND_GREEN, C.white, 0.2));
            ground.addColorStop(1, mix(GROUND_GREEN, C.deep, 0.55));
            ctx.fillStyle = ground;
            ctx.fill();
            // Darker towards the rim, as a curved mirror shows more there.
            const rim = ctx.createRadialGradient(-8, -10, R * 0.3, 0, 0, R);
            rim.addColorStop(0, fade(C.deep, 0));
            rim.addColorStop(1, fade(C.deep, 0.45));
            ctx.fillStyle = rim;
            ctx.fillRect(-R, -R, 2 * R, 2 * R);
            // The highlight: a window's reflection, up and to the left.
            ctx.beginPath();
            ctx.ellipse(-R * 0.36, -R * 0.42, R * 0.22, R * 0.13, -0.5, 0, Math.PI * 2);
            ctx.fillStyle = C.white;
            ctx.fill();
            ctx.restore();
            ball();
            ctx.lineWidth = lw(OUTLINE * 2);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
        },
    },
    {
        name: 'Crowd',
        // Three people standing together, the one at the front biggest: a
        // crowd is people, each a copy of a character.
        draw() {
            const person = (x, y, s, colour) => {
                // The body: shoulders rounding down to the feet.
                ctx.beginPath();
                ctx.moveTo(x - 15 * s, y + 34 * s);
                ctx.lineTo(x - 15 * s, y + 4 * s);
                ctx.quadraticCurveTo(x - 15 * s, y - 8 * s, x, y - 8 * s);
                ctx.quadraticCurveTo(x + 15 * s, y - 8 * s, x + 15 * s, y + 4 * s);
                ctx.lineTo(x + 15 * s, y + 34 * s);
                ctx.closePath();
                ctx.lineWidth = lw(OUTLINE * 2);
                ctx.strokeStyle = C.deep;
                ctx.stroke();
                ctx.fillStyle = colour;
                ctx.fill();
                // The head.
                ctx.beginPath();
                ctx.arc(x, y - 22 * s, 11 * s, 0, Math.PI * 2);
                ctx.stroke();
                ctx.fillStyle = mix(colour, C.white, 0.45);
                ctx.fill();
            };
            ctx.save();
            ctx.lineJoin = 'round';
            person(-24, -4, 0.82, mix(TEAL, C.white, 0.1));
            person(24, -4, 0.82, mix(CUBE_BLUE, C.white, 0.1));
            person(0, 6, 1, YELLOW);
            ctx.restore();
        },
    },
    {
        name: 'Light cookie',
        // A light cookie, a light shining through a pattern: a lamp's beam
        // falling on a floor as a window's four bright panes.
        draw() {
            const view = iconView({ pitch: 40 });
            const at = (x, y, z) => { const p = project(view, [x, y, z]); return [p.x, p.y]; };
            const square = (x0, z0, x1, z1) => [at(x0, 0, z0), at(x1, 0, z0), at(x1, 0, z1), at(x0, 0, z1)];
            const lamp = at(-0.3, 2.3, -0.5);
            ctx.save();
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            // The floor in shade, outlined.
            poly(square(-2.3, -2.3, 2.3, 2.3));
            ctx.fillStyle = mix(C.raised, C.deep, 0.35);
            ctx.fill();
            ctx.lineWidth = lw(OUTLINE * 1.4);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
            // The beam, faint, from the lamp to the patch it lights.
            const patch = square(-1.75, -1.75, 1.75, 1.75);
            poly([lamp, patch[0], patch[1], patch[2], patch[3]]);
            ctx.fillStyle = fade(C.comment, 0.28);
            ctx.fill();
            [0, 1, 2, 3].forEach((i) => {
                ctx.beginPath();
                ctx.moveTo(lamp[0], lamp[1]);
                ctx.lineTo(patch[i][0], patch[i][1]);
                ctx.lineWidth = lw(EDGE);
                ctx.strokeStyle = fade(C.comment, 0.75);
                ctx.stroke();
            });
            // The window's four panes of light, with the frame's shadow between.
            const pane = mix(C.comment, C.white, 0.35);
            [[-1.65, -0.12], [0.12, 1.65]].forEach(([x0, x1]) => [[-1.65, -0.12], [0.12, 1.65]].forEach(([z0, z1]) => {
                poly(square(x0, z0, x1, z1));
                ctx.fillStyle = pane;
                ctx.fill();
                ctx.lineWidth = lw(EDGE);
                ctx.strokeStyle = mix(C.comment, C.deep, 0.35);
                ctx.stroke();
            }));
            // The lamp.
            ctx.beginPath();
            ctx.arc(lamp[0], lamp[1], 12, 0, Math.PI * 2);
            ctx.fillStyle = C.comment;
            ctx.fill();
            ctx.lineWidth = lw(OUTLINE * 1.4);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
            ctx.restore();
        },
    },
    {
        name: 'Water',
        // Water, a pond, a pool, a lake or an ocean: a drop falling into a
        // round pond, with the ripples spreading from where it lands.
        draw() {
            const view = iconView({ pitch: 34 });
            const at = (x, y, z) => { const p = project(view, [x, y, z]); return [p.x, p.y]; };
            const circle = (r) => Array.from({ length: 48 }, (_, i) => {
                const a = i / 48 * Math.PI * 2;
                return at(Math.cos(a) * r, 0, Math.sin(a) * r);
            });
            ctx.save();
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            // The water, outlined, lighter on the far side, where it
            // reflects the sky.
            const R = 2.8, far = at(0, 0, R), near = at(0, 0, -R);
            poly(circle(R));
            ctx.lineWidth = lw(OUTLINE * 2);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
            const surface = ctx.createLinearGradient(0, far[1], 0, near[1]);
            surface.addColorStop(0, mix(CUBE_BLUE, C.white, 0.55));
            surface.addColorStop(1, mix(CUBE_BLUE, C.deep, 0.15));
            ctx.fillStyle = surface;
            ctx.fill();
            // Two ripples round the place the drop lands, fainter further out.
            [[0.75, 0.85], [1.6, 0.6]].forEach(([r, light]) => {
                poly(circle(r));
                ctx.lineWidth = lw(EDGE + 1.5);
                ctx.strokeStyle = mix(CUBE_BLUE, C.white, light);
                ctx.stroke();
            });
            // The drop above them: round below, pointed above, lit from the
            // upper left like the lesson's shapes.
            const o = at(0, 0, 0), r = 14, x = o[0], y = o[1] - 30;
            const drop = () => {
                ctx.beginPath();
                ctx.moveTo(x, y - r * 2.1);
                ctx.bezierCurveTo(x + r * 0.35, y - r * 1.25, x + r, y - r * 0.65, x + r, y);
                ctx.arc(x, y, r, 0, Math.PI);
                ctx.bezierCurveTo(x - r, y - r * 0.65, x - r * 0.35, y - r * 1.25, x, y - r * 2.1);
                ctx.closePath();
            };
            drop();
            ctx.lineWidth = lw(OUTLINE * 2);
            ctx.strokeStyle = C.deep;
            ctx.stroke();
            const body = ctx.createRadialGradient(x - r * 0.4, y - r * 0.5, r * 0.2, x, y - r * 0.3, r * 1.6);
            body.addColorStop(0, mix(CUBE_BLUE, C.white, 0.6));
            body.addColorStop(1, CUBE_BLUE);
            ctx.fillStyle = body;
            ctx.fill();
            // A glint where the light catches it.
            ctx.beginPath();
            ctx.ellipse(x - r * 0.42, y - r * 0.35, r * 0.2, r * 0.32, 0.5, 0, Math.PI * 2);
            ctx.fillStyle = C.white;
            ctx.fill();
            ctx.restore();
        },
    },
];

// The extent of an icon's drawing, found by drawing it on a scratch canvas
// and looking for the pixels it covered.
function extent(icon, scale) {
    const size = 512, cv = document.createElement('canvas');
    cv.width = cv.height = size;
    ctx = cv.getContext('2d');
    ctx.translate(size / 2, size / 2);
    ctx.scale(scale, scale);
    lineScale = 1 / scale;
    icon.draw();
    const data = ctx.getImageData(0, 0, size, size).data;
    let x0 = size, y0 = size, x1 = -1, y1 = -1;
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            if (data[(y * size + x) * 4 + 3] > 12) {
                if (x < x0) x0 = x;
                if (x > x1) x1 = x;
                if (y < y0) y0 = y;
                if (y > y1) y1 = y;
            }
        }
    }
    const to = (v) => (v - size / 2) / scale;
    return { x0: to(x0), y0: to(y0), x1: to(x1 + 1), y1: to(y1 + 1) };
}
// Scales and centres an icon to fill its square. Outlines are part of the
// extent, and keep their width whatever the scale, so the fit is refined
// once with the outlines at their final width.
function fitOf(icon) {
    if (icon.fitted) return icon.fitted;
    const box = icon.fill || FILL;
    let scale = 1;
    for (let pass = 0; pass < 4; pass++) {
        const e = extent(icon, scale);
        scale = box / Math.max(e.x1 - e.x0, e.y1 - e.y0);
    }
    const e = extent(icon, scale);
    icon.fitted = { scale, cx: (e.x0 + e.x1) / 2, cy: (e.y0 + e.y1) / 2 };
    return icon.fitted;
}

function draw(context, index, x, y, size) {
    drawIcon(context, ICONS[index], x, y, size);
}
function drawIcon(context, icon, x, y, size) {
    const fit = fitOf(icon);
    ctx = context;
    ctx.save();
    // Icons share a strip, so none may stray into its neighbour's square.
    ctx.beginPath();
    ctx.rect(x, y, size, size);
    ctx.clip();
    ctx.translate(x, y);
    ctx.scale(size / 128, size / 128);
    ctx.translate(64, 64);
    ctx.scale(fit.scale, fit.scale);
    ctx.translate(-fit.cx, -fit.cy);
    lineScale = 1 / fit.scale;
    icon.draw();
    ctx.restore();
    ctx = null;
    lineScale = 1;
}

// ------------------------------------------------------------ Blip's icons

// The shapes Blip draws his icons with, each in the colour the palette
// gives it unless he chooses one: the World Editor's primitives, and rocks.
const SHAPE_COLOURS = {
    cube: CUBE_BLUE, sphere: YELLOW, cylinder: CAN_RED, cone: CONE_GREEN, capsule: TEAL, tube: CAN_RED,
    torus: SPARK_ORANGE, wedge: CUBE_BLUE, pyramid: YELLOW, prism: CONE_GREEN, arch: mix(CAN_RED, YELLOW, 0.5),
    ring: TEAL, quad: TEAL, rock: RGB(184, 178, 168),
};
// Flat-sided shapes show their edges faintly, as the palette's cube does. A
// rock's many faces show by their shading alone.
const FACETED = ['cube', 'wedge', 'pyramid', 'prism'];

// One of Blip's shapes as an entity: { shape, position, rotation (pitch,
// yaw and roll), scale (a number or [x, y, z]), color ([r, g, b]), edges,
// layer, seed (which rock) }, placed and sized as the World Editor's objects
// are. Hollow shapes are darker inside and lighter on their rims, as the
// palette draws them.
function shapeEnt(shape, index) {
    const colour = Array.isArray(shape.color) ? RGB(shape.color[0], shape.color[1], shape.color[2]) : SHAPE_COLOURS[shape.shape];
    const scale = typeof shape.scale === 'number' ? [shape.scale, shape.scale, shape.scale] : shape.scale || [1, 1, 1];
    const o = {
        pos: shape.position || [0, 0, 0], rot: shape.rotation || [0, 0, 0], scale, colour, layer: shape.layer || 0,
        edges: typeof shape.edges === 'boolean' ? shape.edges : FACETED.includes(shape.shape),
        twoSided: shape.shape === 'quad', segs: shape.shape === 'rock' ? shape.seed || index + 1 : 32,
    };
    if (['tube', 'arch', 'ring'].includes(shape.shape)) o.surf = { 1: colour, 2: mix(colour, C.deep, 0.3), 3: mix(colour, C.white, 0.35) };
    if (shape.shape === 'prism') o.surf = { 1: colour, 2: mix(colour, C.white, 0.3) };
    return ent(shape.shape, o);
}

// Draws Blip's shapes as an icon in a square, as the palette's icons are
// drawn: seen from their view, which view's yaw and pitch may turn, and
// fitted to the same square.
function drawShapes(context, shapes, view, x, y, size) {
    const ents = shapes.map(shapeEnt), look = view || {};
    const options = {};
    if (typeof look.yaw === 'number') options.yaw = look.yaw;
    if (typeof look.pitch === 'number') options.pitch = look.pitch;
    drawIcon(context, { draw() { renderScene(iconView(options), ents); } }, x, y, size);
}

global.WorldEditorIcons = {
    names: ICONS.map((icon) => icon.name), designSize: 128, draw, shapes: Object.keys(SHAPE_COLOURS), drawShapes,
};
})(globalThis);
